import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteNoteMutation } from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';
import { toast } from 'sonner';

/**
 * Shared note deletion for the detail-sheet Notes tabs (Person/Company/Deal).
 *
 * Evicts the Note from the Apollo cache so every cached list drops it
 * reactively, then invokes `onDeleted` (callers pass their notes refetch).
 * Rethrows on failure so DrillDownTable can roll back its optimistic hide.
 * Preview mode (or missing workspace) resolves as a no-op — the table's
 * local hide is the whole preview behavior.
 */
export const useDeleteNote = () => {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const [deleteNoteMutation] = useDeleteNoteMutation();

  const deleteNote = useCallback(async (id: string, onDeleted?: () => void) => {
    if (isLovablePreview() || !workspaceId) return;
    try {
      await deleteNoteMutation({
        variables: { id, workspaceId },
        update: (cache) => {
          cache.evict({ id: cache.identify({ __typename: 'Note', id }) });
          cache.gc();
        },
      });
      toast.success(t('mutations.noteDeleted'));
      onDeleted?.();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error(t('mutations.noteDeleteFailed'));
      throw error;
    }
  }, [workspaceId, deleteNoteMutation, t]);

  return { deleteNote };
};
